import { Button, cx } from "@unizpay/ui";
import { ArrowLeft, Check } from "lucide-react";
import type { FormEvent, ReactNode } from "react";
import { DONE, STEPS } from "./draft";

/**
 * Vertical step list. Steps are not a fixed sequence: any step already reached can be opened
 * again, and `locked` (e.g. no business name yet) keeps the user on the current one.
 */
export function StepNav({
  step,
  reached,
  locked,
  onSelect,
}: {
  step: number;
  reached: number;
  locked: boolean;
  onSelect: (step: number) => void;
}) {
  return (
    <ol className="space-y-1">
      {STEPS.map((s, i) => {
        const current = i === step;
        const complete = step === DONE || (i < reached && !current);
        const open = step !== DONE && i <= reached && !current && !locked;
        return (
          <li key={s.label}>
            <button
              type="button"
              disabled={!open}
              onClick={() => onSelect(i)}
              aria-current={current ? "step" : undefined}
              className={cx(
                "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors",
                current && "bg-subtle",
                open && "hover:bg-subtle",
                !open && !current && "cursor-default",
              )}
            >
              <span
                className={cx(
                  "flex size-8 shrink-0 items-center justify-center rounded-full",
                  complete
                    ? "bg-accent text-accent-fg"
                    : current
                      ? "bg-surface text-accent ring-1 ring-accent"
                      : "bg-surface text-fg-subtle ring-1 ring-line-strong",
                )}
              >
                {complete ? (
                  <Check aria-hidden="true" className="size-4" />
                ) : (
                  <s.icon aria-hidden="true" className="size-4" />
                )}
              </span>
              <span className="min-w-0">
                <span className={cx("block text-sm font-medium", !current && !complete && "text-fg-muted")}>
                  {s.label}
                </span>
                <span className="block text-xs text-fg-muted">{s.text}</span>
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

/** Small screens: a thin progress line and the current step's name instead of the step list. */
export function StepProgress({ step }: { step: number }) {
  const current = STEPS[Math.min(step, STEPS.length - 1)]!;
  return (
    <div>
      <div className="flex gap-1.5" aria-hidden="true">
        {STEPS.map((s, i) => (
          <span
            key={s.label}
            className={cx("h-1 flex-1 rounded-full", i <= step ? "bg-accent" : "bg-line")}
          />
        ))}
      </div>
      <p className="mt-2 text-xs text-fg-muted">
        {step === DONE ? "All set" : `${current.label} · ${current.text}`}
      </p>
    </div>
  );
}

/** One step: a heading, the content, and the actions under a hairline. */
export function StepCard({
  title,
  description,
  children,
  footer,
  onSubmit,
}: {
  title: string;
  description: ReactNode;
  children: ReactNode;
  footer: ReactNode;
  onSubmit?: (e: FormEvent) => void;
}) {
  const body = (
    <section>
      <header className="mb-8">
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        <p className="mt-2 text-sm text-fg-muted">{description}</p>
      </header>
      {children}
      <div className="mt-10 flex flex-wrap items-center justify-end gap-2 border-t border-line pt-6">
        {footer}
      </div>
    </section>
  );
  return onSubmit ? (
    <form onSubmit={onSubmit} noValidate>
      {body}
    </form>
  ) : (
    body
  );
}

export function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <Button variant="ghost" onClick={onClick} className="mr-auto">
      <ArrowLeft aria-hidden="true" /> Back
    </Button>
  );
}
